import React from "react";
import { Link } from "react-router-dom";
import { SEO } from '../components/SEO';
import { SEO_CONTENT } from '../data/seoContent';

const steps = [
  { n: "01", t: "Inquiry & NDA", d: "We start under NDA. Your molecule, route, and data stay yours - protected as trade secrets from the first conversation." },
  { n: "02", t: "Feasibility & route design", d: "Our chemists design a synthetic route engineered for purity and manufacturability, so scale-up is predictable from day one." },
  { n: "03", t: "Lab synthesis & sample", d: "We synthesize at lab scale and offer samples for your evaluation before any commitment to scale." },
  { n: "04", t: "Purification & analytics", d: "In-house purification to electronics-grade (metal impurities < 50 ppb), verified by HPLC, GC-MS and LC-MS with a Certificate of Analysis." },
  { n: "05", t: "Pilot scale-up", d: "We bridge lab to pilot, de-risking the transition with documented process parameters and retained samples." },
  { n: "06", t: "Audited partner manufacturing", d: "Commercial volumes are produced through quality-audited partners selected via our 38-point technical audit, with LAMKO-embedded quality oversight." },
  { n: "07", t: "Supply with CoA & traceability", d: "Consistent, batch-to-batch supply - every lot shipped with a Certificate of Analysis and full traceability." },
];

const programs = [
  { tag: "OLED", t: "Deuterated emitter intermediate", d: "Custom route design and synthesis, sublimed to electronics grade for an OLED materials program." },
  { tag: "Semiconductor", t: "ArF/EUV photoresist monomer", d: "Custom specialty monomer with chromatographic and sublimation purification to sub-ppb metal levels." },
  { tag: "Specialty", t: "High-purity specialty intermediate", d: "Multi-stage purification and analytical validation, scaled through an audited manufacturing partner." },
];

export default function HowWeWork() {
  return (
    <div className="min-h-screen bg-white">
      <SEO {...SEO_CONTENT.howWeWork} />
      {/* Hero */}
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-800 text-white py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">How We Work</h1>
          <p className="text-teal-400 text-lg md:text-xl">
            A transparent, de-risked path from molecule to scaled supply, with one accountable partner at every step.
          </p>
        </div>
      </section>

      {/* Process */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Our engagement flow</h2>
        <p className="text-slate-600 mb-10 max-w-3xl">
          We do the high-value front end in-house - synthesis, electronics-grade purification, and analytics - and scale through an audited global manufacturing network. Here is exactly how a program moves with us. Every step below runs to a written SOP, no improvisation, no shortcuts.
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          {steps.map((s) => (
            <div key={s.n} className="flex gap-4 bg-slate-50 border border-slate-200 rounded-xl p-5">
              <div className="text-teal-600 font-bold text-xl shrink-0">{s.n}</div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">{s.t}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Representative programs */}
      <section className="bg-slate-50 border-y border-slate-200 py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Representative programs</h2>
          <p className="text-slate-500 text-sm mb-8">Illustrative of the work we do. Client identities and details are protected under NDA.</p>
          <div className="grid md:grid-cols-3 gap-5">
            {programs.map((p) => (
              <div key={p.t} className="bg-white border border-slate-200 rounded-xl p-6">
                <span className="inline-block text-xs font-semibold text-teal-700 bg-teal-50 rounded px-2 py-1 mb-3">{p.tag}</span>
                <h3 className="font-semibold text-slate-900 mb-2">{p.t}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / founder */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="bg-slate-900 text-white rounded-2xl p-8 md:p-10">
          <h2 className="text-2xl font-bold mb-3">Scientific leadership</h2>
          <p className="text-slate-300 leading-relaxed">
            LAMKO's programs are led by Dr. Yogesh Kisan Gawale together with specialists across synthesis, purification and analytics, who stay hands-on from first route design to released lot. That chemistry depth is matched with an audited manufacturing network and a documented quality system, so programs scale without changing hands.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-50 border-t border-slate-200 py-14 px-6 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">Start your project</h2>
        <p className="text-slate-600 mb-6 max-w-2xl mx-auto">Tell us the molecule and the grade you need. We&apos;ll respond under NDA with a feasibility view.</p>
        <Link to="/get-in-touch" className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg px-8 py-3 transition-colors">
          Request a consultation →
        </Link>
      </section>
    </div>
  );
}
